import { GenerateUrl } from "@/core/Services/GlobalFunctions";
import ApiService from "@/core/Axios/ApiService";
import {
  CreateResponseMessage,
  LastResponseMessagesObject,
  ResponseMessagesObject,
  LastResponseMessage,
} from "@/modules/ResponseMessages/Services/ResponseMessagesTypes";
import ConfigurationService from "@/core/Services/ConfigurationService";
import DeviceDataService, {
  DeviceLatestData,
} from "@/modules/Devices/Services/DeviceDataService";
import SensorService from "@/modules/Parkom/Submodules/Sensors/Services/SensorService";
import { Sensor } from "@/modules/Parkom/Submodules/Sensors/Services/SensorTypes";

export default class ResponseMessagesService {
  lastDataSent = [] as DeviceLatestData[];
  deviceDataService = new DeviceDataService();
  sensorService = new SensorService();
  sensors = [] as Sensor[];

  async getResponseMessages(
    pageNumber: number = 1,
    pageSize: number = 10
  ): Promise<LastResponseMessagesObject> {
    const url = GenerateUrl({
      path: "/api/company/response-messages/device-assignments",
      prefixPath: ConfigurationService.configData.FIRMWARE_UPDATE_PREFIX_PATH,
      config: {
        PageNumber: pageNumber,
        PageSize: pageSize,
      },
    });

    const response = await ApiService.get(url);

    const uuids = response.data.items.map(
      (responseMessage: LastResponseMessage) => responseMessage.deviceUuid
    );

    try {
      if (uuids.length) {
        this.lastDataSent = await this.deviceDataService.getDevicesLatestData({
          deviceUuids: uuids,
        });
      }
    } catch (error) {
      console.log("Error getting latest sensor data", error);      
    }

    try {
      this.sensors = await this.sensorService.getSensors({
        pageNumber: 1,
        pageSize: 100000,
      });
    } catch (error) {
      console.log("Error getting sensors", error);
    }

    return {
      items: this._mapDevicesLatestData(response.data.items),
      pageIndex: response.data.pageIndex,
      totalPages: response.data.totalPages,
      totalCount: response.data.totalCount,
      hasPreviousPage: response.data.hasPreviousPage,
      hasNextPage: response.data.hasNextPage,
    };
  }

  async getAllAssetResponseMessages(
    pageNumber: number = 1,
    pageSize: number = 10,
    assetId: number
  ): Promise<ResponseMessagesObject> {
    const url = GenerateUrl({
      path: `/api/company/response-messages/device-assignments/${assetId}`,
      prefixPath: ConfigurationService.configData.FIRMWARE_UPDATE_PREFIX_PATH,
      config: {
        PageNumber: pageNumber,
        PageSize: pageSize,
        SortOrder: "Descending",
        SortBy: "CreatedAt",
      },
    });

    const response = await ApiService.get(url);

    return response.data;
  }

  async sendResponseMessage(message: CreateResponseMessage) {
    const url = GenerateUrl({
      path: "/api/company/response-messages",
      prefixPath: ConfigurationService.configData.FIRMWARE_UPDATE_PREFIX_PATH,
    });

    const response = await ApiService.post(url, message);

    return response.data;
  }

  private _mapDevicesLatestData(
    responseMessages: LastResponseMessage[]
  ): LastResponseMessage[] {
    return responseMessages.map((responseMessage) =>
      this._mapDeviceLatestData(responseMessage)
    );
  }

  private _mapDeviceLatestData(
    responseMessage: LastResponseMessage
  ): LastResponseMessage {
    return {
      id: responseMessage.id,
      assetName: responseMessage.assetName,
      deviceId: responseMessage.deviceId,
      deviceUuid: responseMessage.deviceUuid,
      mac: responseMessage.mac,    
      ...(this.sensors.length && {
        parkingSpaceLabel: this.sensors.find(sensor => sensor.deviceUuid == responseMessage.deviceUuid)?.space
      }),
      imei: responseMessage.imei,
      imsi: responseMessage.imsi,
      serialNumber: responseMessage.serialNumber,
      responseMessages: responseMessage.responseMessages,
      createdAt: responseMessage.createdAt,
      updatedAt: responseMessage.updatedAt,
      ...(this.lastDataSent.length && {
        lastTimeDeviceSentData: this.findTheNewestLastTime(
          responseMessage.deviceUuid
        ),
      }),
      ...(this.sensors.length && {
        hwRevision: this.sensors.find(sensor => sensor.deviceUuid == responseMessage.deviceUuid)?.hwRevision
      }),
      ...(this.sensors.length && {
        firmwareVersion: this.sensors.find(sensor => sensor.deviceUuid == responseMessage.deviceUuid)?.firmwareVersion
      }),
      ...(this.lastDataSent.length && {
        firmwareUpdateStatus: this.findFirmwareUpdateStatus(
          responseMessage.deviceUuid
        ),
      })
    };
  }

  private findTheNewestLastTime(deviceUuid: string) {
    const data = this.lastDataSent.find(
      (sentData) => sentData.deviceUuid === deviceUuid
    )?.data;

    let newestDate;
    if (data) {
      newestDate = data.reduce((latest, current) => {
        const latestDate = new Date(latest.dateTime);
        const currentDate = new Date(current.dateTime);

        return latestDate > currentDate ? latest : current;
      }, data[0]).dateTime;
    }

    return newestDate;
  }

  private findFirmwareUpdateStatus(deviceUuid: string) {
    const data = this.lastDataSent.find(
      (sentData) => sentData.deviceUuid === deviceUuid
    )?.data;

    if (data) {
      const newestPacket = data.reduce((latest, current) => {
        return new Date(current.dateTime) > new Date(latest.dateTime) ? current : latest;
      });

      return newestPacket.channel === 4 ? "FW UPDATE":"-";
    } else {
      return "-";
    }
  }
}
